import { EmployerActions } from "../actions/employer.action";
import {
  EwaCreditDecisionConfig,
  PricingConfig,
  SalaryTopupDecisionConfig,
} from "../models/creditDecision";
import { addToLoadingQueue, removeFromLoadingQueue } from "../utils";

export interface EmployerDocument {
  docType: string;
  docStoragePath: string;
}

export interface EmployerConfig {
  maxWithdrawalPercentage?: number;
  minEmploymentDurationForLoanEligibility?: number;
  isDisbursalEnabled?: boolean;
  ewaCreditDecisionConfig?: EwaCreditDecisionConfig;
  pricingConfig?: PricingConfig;
  salaryTopupDecisionConfig?: SalaryTopupDecisionConfig;
}

export interface Employer {
  _id: string;
  name: string;
  clientId: string;
  lenderId: string;
  clientRefId: string;
  segment: string;
  documents: EmployerDocument[];
  config?: EmployerConfig;
  createdAt: string;
  updatedAt: string;
}

export interface EmployerSettings {
  maxWithdrawalSalaryPercent: number | null;
  minRequiredEmploymentDurationInDays?: number | null;
  minRequiredSalary?: number | null;
  isDisbursalAllowed: boolean;
  requiredAgeLimit: {
    min?: number | null;
    max?: number | null;
  };
}

export interface EmployerSettingsPatchDto {
    maxWithdrawalSalaryPercent?: number | null;
    minRequiredEmploymentDurationInDays?: number | null;
    minRequiredSalary?: number | null;
    isDisbursalAllowed?: boolean;
    requiredAgeLimitMin?: number | null;
    requiredAgeLimitMax?: number | null;
}

export interface EmployerState {
  loadingQueue: string[];
  employers: Employer[];
  settings: Record<string, EmployerSettings>;
}

const defaultState: EmployerState = {
  loadingQueue: [],
  employers: [],
  settings: {},
};

export default function EmployerStateReducer(
  state: EmployerState = defaultState,
  action: any
): EmployerState {
  switch (action.type) {
    case EmployerActions.REQUEST_GET_EMPLOYER:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${EmployerActions.REQUEST_GET_EMPLOYER}_SUCCESS`: {
      const employer = action.payload;
      if (!employer?._id) {
        return {
          ...state,
          loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        };
      }
      const exists = state.employers.some((emp) => emp._id === employer._id);
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        employers: exists
          ? state.employers.map((emp) =>
              emp._id === employer._id ? { ...emp, ...employer } : emp
            )
          : [...state.employers, employer],
      };
    }
    case `${EmployerActions.REQUEST_GET_EMPLOYER}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
      };
    case EmployerActions.REQUEST_GET_EMPLOYERS:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${EmployerActions.REQUEST_GET_EMPLOYERS}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        employers: action?.payload,
      };
    case `${EmployerActions.REQUEST_GET_EMPLOYERS}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        employers: [],
      };
    case EmployerActions.REQUEST_GET_EMPLOYER_SETTINGS:
      return {
        ...state,
        loadingQueue: [...state.loadingQueue, "settings"],
      };
    case `${EmployerActions.REQUEST_GET_EMPLOYER_SETTINGS}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        settings: {
          ...state.settings,
          [action.payload.employerId]: action.payload.ewa,
        },
      };
    case `${EmployerActions.REQUEST_GET_EMPLOYER_SETTINGS}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
      };
    case EmployerActions.REQUEST_UPDATE_EMPLOYER_SETTINGS:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${EmployerActions.REQUEST_UPDATE_EMPLOYER_SETTINGS}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        settings: {
          ...state.settings,
          [action.payload.employerId]: action.payload.settings,
        },
      };
    case `${EmployerActions.REQUEST_UPDATE_EMPLOYER_SETTINGS}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
      };
    case EmployerActions.REQUEST_UPDATE_EMPLOYER_CONFIG:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${EmployerActions.REQUEST_UPDATE_EMPLOYER_CONFIG}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        employers: state.employers.map((emp) =>
          emp._id === action.payload._id
            ? {
                ...emp,
                ...action.payload,
                config: action.payload.config ?? emp.config,
              }
            : emp
        ),
      };
    case `${EmployerActions.REQUEST_UPDATE_EMPLOYER_CONFIG}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
      };
    default:
      return state;
  }
}
